import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnChanges, SimpleChanges, ElementRef, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { AuthService, DailyReportService, SysNotifyService, GaugeRefreshService, GmAssignmentService } from '@org/shared-services';
import * as d3 from 'd3';
import { Subscription } from 'rxjs';
import * as moment from 'moment';

@Component({
    selector: 'lib-monthly-closing-gauge',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div class="gauge-container bg-black rounded-lg p-0 h-full flex flex-col">
       

        <!-- SVG Container with labels -->
        <div class="flex-1 w-full relative flex items-center justify-center">
          <!-- Goal Label - Positioned absolutely -->
          <div class="absolute left-0 top-8 max-w-[220px] z-10">
            <div class="flex items-center justify-center border p-2 rounded-md  min-w-[150px]">
              <div class="text-white text-xl font-semibold">Mo. Close Goal</div>
            </div>
            <div class="mt-2">
              <div class="bg-gray-800 px-4 py-2 rounded border border-gray-700 text-center">
                <div class="text-white text-xl font-bold">$\{{ monthlyBudget | number }}</div>
              </div>
            </div>
          </div>

          <!-- Actual Label - Positioned absolutely -->
          <div class="absolute right-0 top-8 max-w-[220px] z-10">
            <div class="flex items-center justify-center border p-2 rounded-md min-w-[150px]">
              <div class="text-white text-xl font-semibold">Actual</div>
            </div>
            <div class="mt-2">
              <div class="bg-gradient-to-b from-blue-600 to-blue-700 px-4 py-2 rounded text-center">
                <div class="text-white text-xl font-bold">$\{{ actualClosed | number }}</div>
              </div>
            </div>
          </div>

          <svg #gaugeSvg 
            class="w-full h-full"
            preserveAspectRatio="xMidYMid meet">
          </svg>
        </div>

        <!-- Footer Stats - made more compact -->
        <div class="flex gap-2 mb-5">
          <!-- Work Days & Days Left Column -->
          <div class="flex flex-col gap-1">
            <!-- Work Days -->
            <div class="bg-[#98c984] rounded-md p-1 text-center w-20">
              <div class="text-black text-xs font-semibold">Work Days</div>
              <div class="text-black text-xl font-bold">{{ workDays }}</div>
            </div>

            <!-- Days Left -->
            <div class="bg-[#ffff00] rounded-md p-1 text-center w-20">
              <div class="text-black text-xs font-semibold">Days Left</div>
              <div class="text-black text-xl font-bold">{{ daysLeft }}</div>
            </div>
          </div>

          <!-- Remaining to Close -->
          <div class="flex-1 flex flex-col">
            <div class="flex items-center justify-center border p-1 rounded-md">
              <div class="text-white text-lg font-semibold">Remaining to Close</div>
            </div>
            <div class="mt-1">
              <div class="bg-[#ffff00] rounded-md p-1 text-center">
                <div class="text-black text-xl font-bold">$\{{ (monthlyBudget - actualClosed) | number }}</div>
              </div>
            </div>
          </div>

          <!-- Missed -->
          <div class="bg-[#4a4a4a] rounded-md p-1 text-center w-20">
            <div class="text-white text-xs font-semibold">Missed</div>
            <div class="text-white text-xl font-bold">{{ missedDays }}</div>
          </div>
        </div>
      </div>
  `,
    styles: [`
    .gauge-container {
      width: 75%;
      min-width: 375px;
      padding: 5px;
      background: #000;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
      margin: 0 auto;
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    .header {
      display: flex;
      justify-content: space-between;
      padding: 0 10px;
    }

    .label-box {
      background: #1a1a1a;
      border: 1px solid #333;
      padding: 5px 15px;
      border-radius: 4px;
      min-width: 150px;
    }

    .label-box.goal {
      box-shadow: 0 2px 4px rgba(255, 255, 255, 0.1);
    }

    .label-box.actual {
      background: linear-gradient(180deg, #0066cc 0%, #004999 100%);
      box-shadow: 0 2px 4px rgba(0, 102, 204, 0.3);
    }

    .label {
      color: #fff;
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 4px;
    }

    .value {
      color: #fff;
      font-size: 18px;
      font-weight: 600;
    }

    svg {
      width: 100%;
      height: auto;
      margin: 0;
    }

    .footer {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 0 0px;
    } 

    .stats-row {
      display: flex;
      justify-content: space-between;
      gap: 10px;
    }

    .stat-box {
      background: #1a1a1a;
      border: 1px solid #333;
      padding: 5px 10px;
      border-radius: 4px;
      flex: 1;
      text-align: center;
    } 
      

    .stat-box.work-days {
      background: linear-gradient(180deg, #5dea75 0%, #3dc956 100%);
    }

    .stat-box.missed {
      background: #333;
    }

    .days-left-box {
      background: #1a1a1a;
      border: 1px solid #333;
      padding: 5px 15px;
      border-radius: 4px;
      text-align: center;
    }

    .value.red {
      color: #ff4444;
      font-size: 24px;
      font-weight: 700;
    }

    .value.yellow {
      color: #ffff00;
      font-size: 20px;
      font-weight: 700;
    }

    
  `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonthlyClosingGaugeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedDate!: string; 
  private svg: any;
  private width = 0;
  private height = 0;
  private radius = 0;
  private centerX = 0;
  private centerY = 0;
  private resizeObserver: ResizeObserver;
  closingGoal: number = 0;
  actualClosed: number = 0;
  daysBehind: number = 0;
  workDays: number = 0;
  daysLeft: number = 0;
  missedDays = 0;
  monthlyBudget: number = 0;
  selectedDateObj: Date = new Date();
  tenantId!: string;
  currentTenant: string | null = null;
  private refreshSubscription: Subscription;

  private getTenantContext(): string {
    if (this.authService.currentUserValue && this.authService.currentUserValue.roles && this.authService.currentUserValue.roles.includes('gm')) {
      return this.currentTenant!;
    }
    return this.tenantId;
  }

  @ViewChild('gaugeSvg', { static: true }) svgElement!: ElementRef;

  constructor(
    private authService: AuthService, 
    private dailyReportService: DailyReportService,
    private sysNotify: SysNotifyService,
    private cdr: ChangeDetectorRef,
    private gaugeRefreshService: GaugeRefreshService,
    private gmAssignmentService: GmAssignmentService
  ) {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateDimensions();
      this.redrawGauge();
    });

    // Subscribe to refresh events
    this.refreshSubscription = this.gaugeRefreshService.refresh$.subscribe(() => {
      this.refreshGauge();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedDate']) {
      const newDate = changes['selectedDate'].currentValue;
      
      if (!newDate) {
        
        return;
      }

      try {
        this.selectedDateObj = new Date(newDate);
        
        if (isNaN(this.selectedDateObj.getTime())) {
          console.error('Invalid date:', newDate);
          return;
        }

       
        this.loadMonthlyReport();
      } catch (error) {
        console.error('Error processing date:', error);
      }
    }
  }

  ngOnInit() {
    this.tenantId = this.authService.currentUserValue?.tenant || '';
    
    if (this.authService.currentUserValue && this.authService.currentUserValue.roles && this.authService.currentUserValue.roles.includes('gm')) {
      this.gmAssignmentService.getCurrentTenant().subscribe((res: any) => {
        this.currentTenant = res.data.tenant;
        this.initAfterTenant();
      });
    } else {
      this.initAfterTenant();
    }
  }

  private initAfterTenant() {
    // Initialize the gauge structure first
    this.resizeObserver.observe(this.svgElement.nativeElement);
    this.initializeGauge();

    // If we have an initial date, load the data
    if (this.selectedDate) {
      this.loadInitialData();
    }
  }

  private loadInitialData() {
    try {
      this.selectedDateObj = new Date(this.selectedDate);
      
      if (isNaN(this.selectedDateObj.getTime())) {
        console.error('Invalid initial date:', this.selectedDate);
        return;
      }

      
      this.loadMonthlyReport();
    } catch (error) {
      console.error('Error processing initial date:', error);
    }
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
    if (this.svg) {
      this.svg.selectAll('*').remove();
    }
    // Clean up subscription
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

 

  private loadMonthlyReport() {
    const selectedMoment = moment(this.selectedDate);
    const year = selectedMoment.year();
    const month = selectedMoment.month() + 1;

    const tenantContext = this.getTenantContext();

    if (tenantContext) {
      this.dailyReportService.getMonthlyReport(tenantContext, year, month).subscribe({
        next: (response: any) => {
          if (response && response.monthTotals) {
            const res = response.monthTotals;
           
            // Set values with fallbacks
            this.closingGoal = res.goalTotal ?? 0;
            this.actualClosed = res.actualTotalEntry ?? 0; //res.cumulativeActualClosed ?? 0;
            this.daysBehind = res.daysBehind ?? 0;
            this.workDays = res.totalWorkingDays ?? this.calculateWorkDaysInMonth();
            this.daysLeft = (res.totalWorkingDays ?? 0) - (res.remainingWorkingDaysOfMonth ?? 0);
            this.missedDays = response.failedProjectionsCount ?? 0;
            this.monthlyBudget = res.monthlyBudget ?? 0;
          } else {
            console.warn('No data available for selected month, using defaults');
            this.setDefaultValues();
          }

          this.redrawGauge();
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading monthly report:', error);
          this.sysNotify.showError('No data available for the selected month');
          this.setDefaultValues();
          this.redrawGauge();
          this.cdr.markForCheck();
        }
      });
    }
  }

  private setDefaultValues() {
    this.closingGoal = 0;
    this.actualClosed = 0;
    this.daysBehind = 0;
    this.workDays = this.calculateWorkDaysInMonth();
  }

  private calculateWorkDaysInMonth(): number {
    const selectedMoment = moment(this.selectedDate);
    const firstDay = selectedMoment.clone().startOf('month');
    const lastDay = selectedMoment.clone().endOf('month');
    let workDays = 0;

    let currentDay = firstDay.clone();
    while (currentDay.isSameOrBefore(lastDay)) {
      if (currentDay.day() !== 0 && currentDay.day() !== 6) { // Skip weekends
        workDays++;
      }
      currentDay.add(1, 'day');
    }

    return workDays;
  }

  private updateDimensions() {
    const container = this.svgElement.nativeElement.parentElement;
    this.width = container.clientWidth;
    this.height = container.clientWidth;
    //this.radius = Math.min(this.width, this.height) / 2;
    this.radius = Math.min(this.width, this.height) * 0.50; 
    this.centerX = this.width / 2;
    this.centerY = this.height / 2;
  }

  private initializeGauge() {
    this.updateDimensions();
    
    this.svg = d3.select(this.svgElement.nativeElement)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${this.width} ${this.height}`);

    // Add definitions for gradients and filters
    this.createDefs();

    // Draw the gauge
    this.drawGauge();
  }

  private createDefs() {
    const defs = this.svg.append('defs');

    // Blue gradient for progress
    const blueGradient = defs.append('linearGradient')
      .attr('id', 'blueGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');

    blueGradient.append('stop')
      .attr('offset', '0%')
      .attr('style', 'stop-color:#4da6ff;stop-opacity:1');
    blueGradient.append('stop')
      .attr('offset', '100%')
      .attr('style', 'stop-color:#0066cc;stop-opacity:1');

    // Green gradient for work days
    const greenGradient = defs.append('linearGradient')
      .attr('id', 'greenGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');

    greenGradient.append('stop')
      .attr('offset', '0%')
      .attr('style', 'stop-color:#9df2ab;stop-opacity:1');
    greenGradient.append('stop')
      .attr('offset', '100%')
      .attr('style', 'stop-color:#5dea75;stop-opacity:1');

    // Add special gradient for today's segment
    const todayGradient = defs.append('linearGradient')
    .attr('id', 'todayGradient')
    .attr('gradientUnits', 'objectBoundingBox')  // Changed from userSpaceOnUse
    .attr('x1', '0%')
    .attr('y1', '0%')
    .attr('x2', '0%')
    .attr('y2', '100%');

    todayGradient.append('stop')
      .attr('offset', '0%')
      .attr('style', 'stop-color:#FFD700;stop-opacity:1'); // Brighter yellow

    todayGradient.append('stop')
      .attr('offset', '100%')
      .attr('style', 'stop-color:#FFA500;stop-opacity:1'); // Orange


    // Add today segment effect (using existing filter structure)
    const todayEffect = defs.append('filter')
      .attr('id', 'today-segment-effect')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');

    todayEffect.append('feGaussianBlur')
      .attr('in', 'SourceGraphic')
      .attr('stdDeviation', '2')
      .attr('result', 'blur');

    todayEffect.append('feColorMatrix')
      .attr('in', 'blur')
      .attr('type', 'matrix')
      .attr('values', '1 0 0 0 1   0 1 0 0 1   0 0 0 0 0  0 0 0 1 0')
      .attr('result', 'glow');

    // // Add yellow glow
    // todayEffect.append('feColorMatrix')
    //   .attr('in', 'offsetBlur')
    //   .attr('type', 'matrix')
    //   .attr('values',
    //     '1 0 0 0 1   0 1 0 0 1   0 0 0 0 0  0 0 0 0.7 0')
    //   .attr('result', 'yellowGlow');

    const todayMerge = todayEffect.append('feMerge');
    todayMerge.append('feMergeNode').attr('in', 'glow');  // Use the existing glow result
    todayMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Enhanced outer arc shadow (darker and more pronounced)
    const outerArcShadow = defs.append('filter')
      .attr('id', 'outer-arc-shadow')
      .attr('width', '200%')
      .attr('height', '200%')
      .attr('x', '-50%')
      .attr('y', '-50%');

    // Create stronger inner shadow
    outerArcShadow.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', '4')
      .attr('result', 'blur');

    outerArcShadow.append('feOffset')
      .attr('in', 'blur')
      .attr('dx', '0')
      .attr('dy', '6')
      .attr('result', 'offsetBlur');

    // Make shadow darker
    outerArcShadow.append('feColorMatrix')
      .attr('in', 'offsetBlur')
      .attr('type', 'matrix')
      .attr('values',
        '0 0 0 0 0   0 0 0 0 0   0 0 0 0 0  0 0 0 0.99 0')
      .attr('result', 'darkShadow');

    // Inner shadow for depth
    outerArcShadow.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', '4')
      .attr('result', 'innerBlur');

    outerArcShadow.append('feOffset')
      .attr('in', 'innerBlur')
      .attr('dx', '0')
      .attr('dy', '1')
      .attr('result', 'innerOffset');

    const shadowMerge = outerArcShadow.append('feMerge');
    shadowMerge.append('feMergeNode').attr('in', 'darkShadow');
    shadowMerge.append('feMergeNode').attr('in', 'innerOffset');
    shadowMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Enhance progress arc shadow for overlaying effect
    const progressArcShadow = defs.append('filter')
      .attr('id', 'progress-arc-shadow')
      .attr('width', '200%')
      .attr('height', '200%')
      .attr('x', '-50%')
      .attr('y', '-50%');

    // Create stronger shadow for progress arc
    progressArcShadow.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', '6')  // Increased blur
      .attr('result', 'blur');

    progressArcShadow.append('feOffset')
      .attr('in', 'blur')
      .attr('dx', '0')
      .attr('dy', '8')
      .attr('result', 'offsetBlur');

    // Dark shadow for depth
    progressArcShadow.append('feColorMatrix')
      .attr('in', 'offsetBlur')
      .attr('type', 'matrix')
      .attr('values',
        '0 0 0 0 0   0 0 0 0 0   0 0 0 0 0  0 0 0 0.99 0')  // Darker shadow
      .attr('result', 'darkShadow');

    // Add subtle blue glow
    progressArcShadow.append('feColorMatrix')
      .attr('in', 'offsetBlur')
      .attr('type', 'matrix')
      .attr('values',
        '0 0 0 0 0.1   0 0 0 0 0.3   0 0 0 0 0.6  0 0 0 0.2 0')
      .attr('result', 'blueGlow');

    const progressShadowMerge = progressArcShadow.append('feMerge');
    progressShadowMerge.append('feMergeNode').attr('in', 'darkShadow');
    progressShadowMerge.append('feMergeNode').attr('in', 'blueGlow');
    progressShadowMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Center circle shadow
    const centerShadow = defs.append('filter')
      .attr('id', 'center-shadow')
      .attr('width', '200%')
      .attr('height', '200%')
      .attr('x', '-50%')
      .attr('y', '-50%');

    centerShadow.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', '3')
      .attr('result', 'blur');

    centerShadow.append('feOffset')
      .attr('in', 'blur')
      .attr('dx', '0')
      .attr('dy', '8')
      .attr('result', 'offsetBlur');

    centerShadow.append('feColorMatrix')
      .attr('in', 'offsetBlur')
      .attr('type', 'matrix')
      .attr('values',
        '0 0 0 0 0   0 0 0 0 0   0 0 0 0 0  0 0 0 0.95 0')
      .attr('result', 'darkShadow');

    const centerShadowMerge = centerShadow.append('feMerge');
    centerShadowMerge.append('feMergeNode').attr('in', 'darkShadow');
    centerShadowMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Add gradient for outer arc (3D effect)
    const outerArcGradient = defs.append('linearGradient')
      .attr('id', 'outerArcGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');

    outerArcGradient.append('stop')
      .attr('offset', '0%')
      .attr('style', 'stop-color:#606060;stop-opacity:1'); // Lighter grey
    outerArcGradient.append('stop')
      .attr('offset', '100%')
      .attr('style', 'stop-color:#303030;stop-opacity:1'); // Darker grey

    // Add inner bevel effect for outer arc
    const outerArcBevel = defs.append('filter')
      .attr('id', 'outer-arc-bevel')
      .attr('width', '200%')
      .attr('height', '200%')
      .attr('x', '-50%')
      .attr('y', '-50%');

    // Create highlight
    outerArcBevel.append('feSpecularLighting')
      .attr('in', 'SourceGraphic')
      .attr('surfaceScale', '5')
      .attr('specularConstant', '1')
      .attr('specularExponent', '20')
      .attr('lighting-color', '#FFFFFF')
      .append('fePointLight')
      .attr('x', '50')
      .attr('y', '-50')
      .attr('z', '100');

    // Composite the bevel effect
    outerArcBevel.append('feComposite')
      .attr('in2', 'SourceGraphic')
      .attr('operator', 'in')
      .attr('result', 'bevel');

    // Merge with existing shadow
    const outerArcMerge = outerArcBevel.append('feMerge');
    outerArcMerge.append('feMergeNode').attr('in', 'bevel');
    outerArcMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Regular drop shadow filter (also update this for consistency)
    const dropShadow = defs.append('filter')
      .attr('id', 'drop-shadow')
      .attr('width', '150%')
      .attr('height', '150%');

    dropShadow.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', '4')
      .attr('result', 'blur');

    dropShadow.append('feOffset')
      .attr('in', 'blur')
      .attr('dx', '2')
      .attr('dy', '4')
      .attr('result', 'offsetBlur');

    // Add dark shadow
    dropShadow.append('feColorMatrix')
      .attr('in', 'offsetBlur')
      .attr('type', 'matrix')
      .attr('values',
        '0 0 0 0 0    0 0 0 0 0    0 0 0 0 0   0 0 0 0.7 0')
      .attr('result', 'darkBlur');

    const dropShadowMerge = dropShadow.append('feMerge');
    dropShadowMerge.append('feMergeNode').attr('in', 'darkBlur');
    dropShadowMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Enhanced inset effect for center circle
    const centerInset = defs.append('filter')
      .attr('id', 'center-inset')
      .attr('width', '200%')
      .attr('height', '200%')
      .attr('x', '-50%')
      .attr('y', '-50%');

    // Create deeper inner shadow
    centerInset.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', '4')  // Increased blur
      .attr('result', 'blur');

    // Top shadow (darker and more offset)
    centerInset.append('feOffset')
      .attr('in', 'blur')
      .attr('dx', '0')
      .attr('dy', '-6')  // Increased offset
      .attr('result', 'offsetTop');

    centerInset.append('feColorMatrix')
      .attr('in', 'offsetTop')
      .attr('type', 'matrix')
      .attr('values',
        '0 0 0 0 0   0 0 0 0 0   0 0 0 0 0  0 0 0 0.95 0')  // Increased opacity
      .attr('result', 'topShadow');

    // Bottom highlight (stronger)
    centerInset.append('feOffset')
      .attr('in', 'blur')
      .attr('dx', '0')
      .attr('dy', '6')  // Increased offset
      .attr('result', 'offsetBottom');

    centerInset.append('feColorMatrix')
      .attr('in', 'offsetBottom')
      .attr('type', 'matrix')
      .attr('values',
        '1 0 0 0 0.1   1 0 0 0 0.1   1 0 0 0 0.1  0 0 0 0.4 0')  // Adjusted for better highlight
      .attr('result', 'bottomHighlight');

    // Enhanced bevel effect
    centerInset.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', '3')  // Increased blur for softer bevel
      .attr('result', 'bevelBlur');

    // Add two lighting effects for more dramatic bevel
    const specular1 = centerInset.append('feSpecularLighting')
      .attr('in', 'bevelBlur')
      .attr('surfaceScale', '-5')  // More pronounced depth
      .attr('specularConstant', '1.5')  // Increased reflection
      .attr('specularExponent', '12')
      .attr('lighting-color', '#555555');  // Lighter color for better contrast

    specular1.append('fePointLight')
      .attr('x', '-100')
      .attr('y', '-100')
      .attr('z', '150');  // Increased z for stronger effect

    // Second lighting effect for rim highlight
    const specular2 = centerInset.append('feSpecularLighting')
      .attr('in', 'bevelBlur')
      .attr('surfaceScale', '-2')
      .attr('specularConstant', '1')
      .attr('specularExponent', '8')
      .attr('lighting-color', '#222222')
      .attr('result', 'rimLight');

    specular2.append('fePointLight')
      .attr('x', '100')
      .attr('y', '100')
      .attr('z', '100');

    // Merge all effects in specific order
    const centerMerge = centerInset.append('feMerge');
    centerMerge.append('feMergeNode').attr('in', 'topShadow');
    centerMerge.append('feMergeNode').attr('in', 'bottomHighlight');
    centerMerge.append('feMergeNode').attr('in', 'SourceGraphic');
    centerMerge.append('feMergeNode').attr('in', 'specularOut');
    centerMerge.append('feMergeNode').attr('in', 'rimLight');

    // Add text effects filter
    const textEffect = defs.append('filter')
      .attr('id', 'text-effect')
      .attr('width', '200%')
      .attr('height', '200%')
      .attr('x', '-50%')
      .attr('y', '-50%');

    // Create text shadow
    textEffect.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', '2')
      .attr('result', 'blur');

    textEffect.append('feOffset')
      .attr('in', 'blur')
      .attr('dx', '0')
      .attr('dy', '2')
      .attr('result', 'offsetBlur');

    // Add lighting effect
    const textLighting = textEffect.append('feSpecularLighting')
      .attr('in', 'blur')
      .attr('surfaceScale', '5')
      .attr('specularConstant', '.75')
      .attr('specularExponent', '20')
      .attr('lighting-color', '#ffffff')
      .attr('result', 'specOut');

    textLighting.append('fePointLight')
      .attr('x', '50')
      .attr('y', '-50')
      .attr('z', '200');

    // Create composite effects
    textEffect.append('feComposite')
      .attr('in', 'specOut')
      .attr('in2', 'SourceAlpha')
      .attr('operator', 'in')
      .attr('result', 'specular');

    const feMerge = textEffect.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'offsetBlur');
    feMerge.append('feMergeNode').attr('in', 'specular');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Enhanced red circle effect with 3D and glow
    const redCircleEffect = defs.append('filter')
      .attr('id', 'red-circle-effect')
      .attr('width', '200%')
      .attr('height', '200%')
      .attr('x', '-50%')
      .attr('y', '-50%');

    // Base shadow
    redCircleEffect.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', '4')
      .attr('result', 'blur');

    redCircleEffect.append('feOffset')
      .attr('in', 'blur')
      .attr('dx', '0')
      .attr('dy', '4')
      .attr('result', 'offsetBlur');

    // Add lighting effect for 3D
    const redLighting = redCircleEffect.append('feSpecularLighting')
      .attr('in', 'SourceGraphic')
      .attr('surfaceScale', '5')
      .attr('specularConstant', '1.2')
      .attr('specularExponent', '20')
      .attr('lighting-color', '#ffffff')
      .attr('result', 'specOut');

    redLighting.append('fePointLight')
      .attr('x', '-50')
      .attr('y', '-50')
      .attr('z', '100');

    // Create composite effects
    redCircleEffect.append('feComposite')
      .attr('in', 'specOut')
      .attr('in2', 'SourceAlpha')
      .attr('operator', 'in')
      .attr('result', 'specular');

    // Add red glow
    redCircleEffect.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', '5')
      .attr('result', 'glow');

    // Color the glow red
    redCircleEffect.append('feColorMatrix')
      .attr('in', 'glow')
      .attr('type', 'matrix')
      .attr('values',
        '1 0 0 0 1   0 0 0 0 0   0 0 0 0 0  0 0 0 0.5 0')
      .attr('result', 'redGlow');

    // Merge all effects
    const redMerge = redCircleEffect.append('feMerge');
    redMerge.append('feMergeNode').attr('in', 'offsetBlur');
    redMerge.append('feMergeNode').attr('in', 'redGlow');
    redMerge.append('feMergeNode').attr('in', 'specular');
    redMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Add gradient for 3D effect
    const redGradient = defs.append('radialGradient')
      .attr('id', 'redCircleGradient')
      .attr('cx', '40%')
      .attr('cy', '40%')
      .attr('r', '60%');

    redGradient.append('stop')
      .attr('offset', '0%')
      .attr('style', 'stop-color:#ff4444;stop-opacity:1');
    redGradient.append('stop')
      .attr('offset', '100%')
      .attr('style', 'stop-color:#cc0000;stop-opacity:1');
  }

  private drawGauge() { 
    const progress = this.monthlyBudget > 0 ? (this.actualClosed / this.monthlyBudget) : 0;

    // Define arcs with new starting point at top (12 o'clock)
    const outerArc = d3.arc()
      .innerRadius(this.radius * 0.65)
      .outerRadius(this.radius * 0.9)
      .startAngle(0)          // Start at top
      .endAngle(2 * Math.PI); // Full circle

    const progressArc = d3.arc()
      .innerRadius(this.radius * 0.65)
      .outerRadius(this.radius * 0.9)
      .startAngle(0)          // Start at top
      .endAngle(2 * Math.PI * progress); // Progress clockwise

    // Draw elements from bottom to top
    
    // 1. First draw the center circle with inset effect
    this.svg.append('circle')
      .attr('cx', this.centerX)
      .attr('cy', this.centerY)
      .attr('r', this.radius * 0.4)
      .style('fill', '#252525')  // Darker base color for more contrast
      .style('filter', 'url(#center-inset)');

    // Add two inner rings for enhanced depth perception
    this.svg.append('circle')
      .attr('cx', this.centerX)
      .attr('cy', this.centerY)
      .attr('r', this.radius * 0.39)
      .style('fill', 'none')
      .style('stroke', '#1a1a1a')
      .style('stroke-width', '2px');

    this.svg.append('circle')
      .attr('cx', this.centerX)
      .attr('cy', this.centerY)
      .attr('r', this.radius * 0.37)
      .style('fill', 'none')
      .style('stroke', '#303030')
      .style('stroke-width', '1px');

    // Calculate working days in the selected month
    const selectedMoment = moment(this.selectedDate);
    const firstDayOfMonth = selectedMoment.clone().startOf('month');
    const lastDayOfMonth = selectedMoment.clone().endOf('month');
    const today = moment();
    
    // Create array of only workdays in month
    const workdaysInMonth = [];
    let currentDay = firstDayOfMonth.clone();
    
    while (currentDay.isSameOrBefore(lastDayOfMonth)) {
      if (currentDay.day() !== 0 && currentDay.day() !== 6) { // Skip weekends
        workdaysInMonth.push({
          date: currentDay.clone(),
          isCompleted: currentDay.isBefore(today, 'day'), // Previous days are completed
          isToday: currentDay.isSame(today, 'day') // Check if it's today
        });
      }
      currentDay.add(1, 'day');
    }

    const totalWorkDays = workdaysInMonth.length;
    const anglePerDay = (2 * Math.PI) / totalWorkDays;

    // 2. Animate work day segments
    const workDayArc = d3.arc()
      .innerRadius(this.radius * 0.4)
      .outerRadius(this.radius * 0.65);

    // Draw segments for workdays only
    workdaysInMonth.forEach((day, i) => {
      const startAngle = i * anglePerDay;
      const endAngle = startAngle + anglePerDay;
      
      const segment = this.svg.append('path')
        .datum({
          startAngle: startAngle,
          endAngle: endAngle
        });

      // Add glow effect for current day
      if (day.isToday) {
        segment
          .style('fill', 'url(#todayGradient)')
          .style('filter', 'url(#today-segment-effect)')
          .style('stroke', '#ffffff')
          .style('stroke-width', '2px')
          .style('opacity', 0);  // Add initial opacity
      } else {
        segment
          .style('fill', day.isCompleted ? 'url(#greenGradient)' : '#404040')
          .style('stroke', '#1a1a1a')
          .style('stroke-width', '1px')
          .style('opacity', 0);  // Add initial opacity
      }

      segment
        .attr('transform', `translate(${this.centerX},${this.centerY})`)
        .attr('d', workDayArc)
        .transition()
        .duration(20)
        .delay(i * 15)
        .style('opacity', 1);

      // Add date tooltip
      segment.append('title')
        .text(day.date.format('MM/DD/YYYY') + 
              (day.isToday ? ' (Today)' : 
               day.isCompleted ? ' (Completed)' : ' (Upcoming)'));
    });

    // 3. Draw and animate outer arc
    const outerArcPath = this.svg.append('path')
      .datum({ endAngle: 0 })  // Start from top
      .style('fill', '#404040')
      .style('filter', 'url(#outer-arc-shadow)')
      .attr('transform', `translate(${this.centerX},${this.centerY})`)
      .attr('d', outerArc);

    outerArcPath.transition()
      .duration(1000)
      .ease(d3.easeQuadOut)
      .attrTween('d', (d: any) => {
        const interpolate = d3.interpolate(d.endAngle, 2 * Math.PI); // Full circle
        return (t: number) => {
          d.endAngle = interpolate(t);
          return outerArc(d);
        };
      });

    // 4. Draw and animate progress arc
    const progressPath = this.svg.append('path')
      .datum({ endAngle: 0 })  // Start from top
      .style('fill', 'url(#blueGradient)')
      .style('filter', 'url(#progress-arc-shadow)')
      .attr('transform', `translate(${this.centerX},${this.centerY})`)
      .attr('d', progressArc);

    progressPath.transition()
      .duration(1500)
      .ease(d3.easeElasticOut.amplitude(1).period(0.5))
      .delay(500)
      .attrTween('d', (d: any) => {
        const interpolate = d3.interpolate(
          d.endAngle,
          2 * Math.PI * progress  // Progress clockwise from top
        );
        return (t: number) => {
          d.endAngle = interpolate(t);
          return progressArc(d);
        };
      })
      .transition()
      .duration(1000)
      .ease(d3.easeBounce)
      .attrTween('d', (d: any) => {
        const endAngle = 2 * Math.PI * progress;
        const interpolate = d3.interpolate(
          endAngle - 0.1,
          endAngle
        );
        return (t: number) => {
          d.endAngle = interpolate(t);
          return progressArc(d);
        };
      });

    // Create text group
    const textGroup = this.svg.append('g')
      .attr('transform', `translate(${this.centerX},${this.centerY})`);

    // Add red circle with enhanced 3D effect
    textGroup.append('circle')
      .attr('r', 0)  // Start from size 0
      .style('fill', 'url(#redCircleGradient)')
      .style('filter', 'url(#red-circle-effect)')
      .lower()
      .transition()
      .duration(400)
      .ease(d3.easeBounceOut)
      .attr('r', this.radius * 0.20);

    // Animate text elements
    // "Days" text
    textGroup.append('text')
      .attr('y', -55)
      .attr('text-anchor', 'middle')
      .attr('class', 'label-text')
      .style('fill', '#ffffff')
      .style('font-size', '36px')
      .style('font-family', 'Arial, sans-serif')
      .style('font-weight', '600')
      .style('letter-spacing', '0.5px')
      .style('filter', 'url(#text-effect)')
      .style('opacity', 0)
      .text('Days')
      .transition()
      .duration(300)
      .delay(400)
      .style('opacity', 1);

    // Number with counting animation
    const valueText = textGroup.append('text')
      .attr('y', 25)
      .attr('text-anchor', 'middle')
      .attr('class', 'value-text')
      .style('fill', '#ffffff')
      .style('font-size', '74px')
      .style('font-family', 'Arial, sans-serif')
      .style('font-weight', '700')
      .style('letter-spacing', '1px')
      .style('filter', 'url(#text-effect)')
      .text('0');

    // Animate number counting up
    valueText.transition()
      .duration(1000)
      .delay(400)
      .tween('text', () => {
        const interpolator = d3.interpolateNumber(0, this.daysBehind);
        return (t: number) => {
          valueText.text(Math.round(interpolator(t)));
        };
      });

    // "Behind" text
    textGroup.append('text')
      .attr('y', 70)
      .attr('text-anchor', 'middle')
      .attr('class', 'label-text')
      .style('fill', '#ffffff')
      .style('font-size', '36px')
      .style('font-family', 'Arial, sans-serif')
      .style('font-weight', '600')
      .style('letter-spacing', '0.5px')
      .style('filter', 'url(#text-effect)')
      .style('opacity', 0)
      .text('Behind')
      .transition()
      .duration(500)
      .delay(800)
      .style('opacity', 1);

       // Add "No Data Available" text if needed
    if (this.closingGoal === 0 && this.actualClosed === 0) {
      this.svg.append('text')
        .attr('x', this.centerX)
        .attr('y', this.centerY + 100)
        .attr('text-anchor', 'middle')
        .style('fill', '#999')
        .style('font-size', '14px')
        .text('No data available for selected month');
    }
  }

  private redrawGauge() {
    if (!this.svg) {
      console.warn('SVG not initialized');
      return;
    }
    
    // Clear existing gauge
    this.svg.selectAll('*').remove();
    
    // Redraw gauge with current data 
   // this.drawGauge();
    this.initializeGauge();
  }

    // Update the isSameDay method to use moment
    private isSameDay(d1: moment.Moment, d2: moment.Moment): boolean {
      return d1.isSame(d2, 'day');
    }

    // Add method to refresh gauge data
    refreshGauge() {
      this.loadMonthlyReport();
    }
} 